import { FC, useState } from 'react';
import { ReactComponent as AddIcon } from 'assets/icons/add_2.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import styles from './InsuranceUpload.module.scss';
import { renderToast } from 'components/CustomToast/CustomToast';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import cx from 'classnames';
import heic2any from 'heic2any';
import { CircularProgress } from '@material-ui/core';

interface InsuranceUploadProps {
  id: string;
  file: File | null;
  setFile: (file: File | null) => void;
}

const ERROR_MESSAGE =
  'Unsupported file type. Please upload an image file (png, jpg, jpeg).';

const InsuranceUpload: FC<InsuranceUploadProps> = ({ file, setFile, id }) => {
  const isLoadInsideIframe = useIsLoadedInsideIframe();
  const [isUploading, setIsUploading] = useState(false);

  const uploadImagecontainer = cx({
    [styles['uploaded-image-container']]: true,
    [styles['uploaded-image-container-iframe']]: isLoadInsideIframe,
  });

  const uploadBtnContainer = cx({
    [styles['upload-btn']]: true,
    [styles['upload-btn-iframe']]: isLoadInsideIframe,
  });

  const compressAndConvertToWebP = async (file: File): Promise<File> => {
    let processedFile = file;

    // Convert HEIC/HEIF to JPEG first
    if (file.type === 'image/heic' || file.type === 'image/heif') {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9,
        });

        processedFile = new File(
          [convertedBlob as Blob],
          file.name.replace(/\.[^/.]+$/, '.jpg'),
          { type: 'image/jpeg' }
        );
      } catch (error) {
        throw new Error(ERROR_MESSAGE);
      }
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(processedFile);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement('canvas');

          const maxSize = 1920;
          let width = img.width;
          let height = img.height;

          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const webpFile = new File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, '.webp'),
                  { type: 'image/webp' }
                );
                resolve(webpFile);
              } else {
                reject(new Error('Failed to convert to WebP'));
              }
            },
            'image/webp',
            0.7
          );
        };

        img.onerror = () => reject(new Error('Failed to load image'));
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const onFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    const allowedTypes = [
      'image/png',
      'image/jpg',
      'image/jpeg',
      'image/avif',
      'image/webp',
      'image/heif',
      'image/heic',
    ];

    if (!allowedTypes.includes(uploadedFile.type)) {
      renderToast({
        type: 'warning',
        message: ERROR_MESSAGE,
      });
      return;
    }

    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (uploadedFile.size > maxSizeInBytes) {
      renderToast({
        type: 'warning',
        message: 'File size exceeds 10MB. Please upload a smaller file.',
      });
      return;
    }

    setIsUploading(true);
    try {
      const webpFile = await compressAndConvertToWebP(uploadedFile);
      setFile(webpFile);
    } catch (error) {
      const messageError =
        error instanceof Error
          ? error.message
          : 'File upload failed. Please try again.';
      renderToast({
        type: 'warning',
        message: messageError,
      });
    }
    setIsUploading(false);
  };

  return (
    <>
      {file ? (
        <div className={uploadImagecontainer}>
          <img
            src={URL.createObjectURL(file)}
            alt={`${id}-uploaded-preview`}
            className={styles['uploaded-image']}
          />
          <button
            className={styles['remove-image-btn']}
            onClick={() => setFile(null)}
          >
            <CloseIcon />
          </button>
        </div>
      ) : (
        <button className={uploadBtnContainer}>
          <input
            hidden
            type="file"
            name="insuranceFront"
            accept="png, jpg, jpeg, avif, webp"
            onChange={onFileUpload}
            id={id}
          />
          <label htmlFor={id}>
            {isUploading ? <CircularProgress /> : <AddIcon />}
          </label>
        </button>
      )}
    </>
  );
};

export default InsuranceUpload;
